import React, { useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  fetchDetailedNews,
  fetchShortNews,
  deleteDetailedNews,
  deleteShortNews
} from "../services/newsService";

import { IMAGE_BASE_URL } from "../services/imageConfig";

import Header from "../components/Header";
import Button from "../components/Button";
import Loader from "../components/Loader";
import Item from "../components/Item";
import DetailedNewsForm from "../components/DetailedNewsForm";
import ShortNewsForm from "../components/ShortNewsForm";
import Modal from "../components/Modal";
import "../styles/pages/news_page.css";

const processImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  const normalized = url.startsWith("/") ? url : `/${url}`;
  return `${IMAGE_BASE_URL}${normalized}`;
};

const NewsPage = () => {
  const [newsData, setNewsData] = useState({ detailed: [], short: [] });
  const [showForm, setShowForm] = useState({ detailed: false, short: false });
  const [formMode, setFormMode] = useState({ detailed: "create", short: "create" });
  const [currentData, setCurrentData] = useState({ detailed: null, short: null });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadNews = useCallback(async () => {
    setIsLoading(true);
    try {
      const [detailedRaw, shortRaw] = await Promise.all([
        fetchDetailedNews().catch((err) => {
          if (err?.response?.status === 404) return [];
          if (err?.response?.status === 403) throw err;
          throw err;
        }),
        fetchShortNews().catch((err) => {
          if (err?.response?.status === 404) return [];
          if (err?.response?.status === 403) throw err;
          throw err;
        })
      ]);

      const detailed = Array.isArray(detailedRaw) ? detailedRaw : [];
      const short = Array.isArray(shortRaw) ? shortRaw : [];

      setNewsData({
        detailed: detailed.map((item) => ({
          ...item,
          titleImage: processImageUrl(item.titleImage),
          imageFirst: processImageUrl(item.imageFirst),
        })),
        short: short.map((item) => ({
          ...item,
          image: processImageUrl(item.image),
        })),
      });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Время сессии истекло, пожалуйста войдите заново", {
          position: "bottom-left",
          autoClose: 3000,
        });
      } else {
        toast.error("Ошибка загрузки новостей", {
          position: "bottom-left",
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  const handleEdit = (item, type) => {
    setFormMode((prev) => ({ ...prev, [type]: "edit" }));
    setCurrentData((prev) => ({ ...prev, [type]: item }));
    setShowForm((prev) => ({ ...prev, [type]: true }));
  };

  const handleDelete = (item, type) => {
    setDeleteTarget({ item, type });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "detailed") {
        await deleteDetailedNews(deleteTarget.item.id);
      } else {
        await deleteShortNews(deleteTarget.item.id);
      }
      toast.success("Новость успешно удалена", {
        position: "bottom-left",
        autoClose: 3000,
      });
      await loadNews();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Время сессии истекло, пожалуйста войдите заново", {
          position: "bottom-left",
          autoClose: 3000,
        });
      } else {
        toast.error("Ошибка удаления новости", {
          position: "bottom-left",
          autoClose: 3000,
        });
      }
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleCreateClick = (type) => {
    setFormMode((prev) => ({ ...prev, [type]: "create" }));
    setCurrentData((prev) => ({ ...prev, [type]: null }));
    setShowForm((prev) => ({ ...prev, [type]: true }));
  };

  return (
    <div className="news-page">
      <Header />
      <ToastContainer />

      {isLoading ? (
        <div className="loader-wrapper">
          <Loader />
          <p className="loading-text">Загрузка новостей...</p>
        </div>
      ) : (
        <div className="news-page_content">
          <div className="detailed-news_section">
            <h3 className="section-title">Список подробных новостей</h3>
            <div className="items-container">
              {newsData.detailed.length > 0 ? (
                newsData.detailed.map((item, index) => (
                  <div key={item.id} className="numbered-item">
                    <span className="item-number">{index + 1}</span>
                    <Item
                      imageSrc={item.titleImage}
                      title={item.title}
                      onEdit={() => handleEdit(item, "detailed")}
                      onDelete={() => handleDelete(item, "detailed")}
                    />
                  </div>
                ))
              ) : (
                <p className="empty-list-label">Список пуст</p>
              )}
              <Button
                text="+ Добавить"
                onClick={() => handleCreateClick("detailed")}
              />
            </div>
          </div>

          <div className="short-news_section">
            <h3 className="section-title">Список кратких новостей</h3>
            <div className="items-container">
              {newsData.short.length > 0 ? (
                newsData.short.map((item, index) => (
                  <div key={item.id} className="numbered-item">
                    <span className="item-number">{index + 1}</span>
                    <Item
                      imageSrc={item.image}
                      title={item.title}
                      onEdit={() => handleEdit(item, "short")}
                      onDelete={() => handleDelete(item, "short")}
                    />
                  </div>
                ))
              ) : (
                <p className="empty-list-label">Список пуст</p>
              )}
              <Button
                text="+ Добавить"
                onClick={() => handleCreateClick("short")}
              />
            </div>
          </div>
        </div>
      )}

      {showForm.detailed && (
        <DetailedNewsForm
          mode={formMode.detailed}
          initialData={currentData.detailed}
          onClose={() => setShowForm((prev) => ({ ...prev, detailed: false }))}
          onSuccess={loadNews}
        />
      )}

      {showForm.short && (
        <ShortNewsForm
          mode={formMode.short}
          initialData={currentData.short}
          onClose={() => setShowForm((prev) => ({ ...prev, short: false }))}
          onSuccess={loadNews}
        />
      )}

      <Modal
        isOpen={showDeleteModal}
        message="Вы действительно хотите удалить новость?"
        itemTitle={deleteTarget?.item?.title}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default NewsPage;
